import { useEffect, useState } from 'react';
import http from '../../api/axios';
import Button from '../../components/button/Button';
import { toast } from 'react-toastify';
import { useLoading } from '../../context/loadingContext';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from '../../context/translationContext';

interface Props {
  onUpload?: () => void;
  apiPostFilesUrl?: string;
  showUploadButton?: boolean;
  selectedFile?: File[];
  setSelectedFile?: (file: File[]) => void;
}

/**
 * File upload component
 */
export const FileUpload = ({
  onUpload,
  apiPostFilesUrl,
  showUploadButton = true,
  selectedFile,
  setSelectedFile,
}: Props) => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDrop: (files) => {
      setAcceptedFiles(files);
    },
  });
  const { loading, startLoading, stopLoading } = useLoading();
  const { t } = useTranslation();

  useEffect(() => {
    if (!setSelectedFile || showUploadButton) return;
    setSelectedFile(acceptedFiles);
  }, [acceptedFiles]);

  // reset selected file
  useEffect(() => {
    if (!selectedFile || selectedFile?.length === acceptedFiles.length) return;
    if (selectedFile && selectedFile.length === 0) setAcceptedFiles([]);
  }, [selectedFile]);

  /**
   * Uploads selected file
   */
  const handleOnSubmitAttachment = async () => {
    if (!acceptedFiles || acceptedFiles.length === 0 || !apiPostFilesUrl) return;
    try {
      startLoading();
      const formData = new FormData();
      // can upload only 1 file
      formData.append('file', acceptedFiles[0]);
      const response = await http.apiPostFiles({
        url: apiPostFilesUrl,
        data: formData,
      });
      if (response?.status === 200) {
        stopLoading();
        setAcceptedFiles([]);
        toast.success('Success!');
        onUpload && onUpload();
      }
    } catch (err) {
      stopLoading();
      toast.error('error');
    }
  };

  return (
    <>
      <div className="flex items-center w-full">
        <div
          {...getRootProps()}
          className="border border-gray-400 border-dashed px-4 py-2.5 w-full text-center hover:cursor-pointer"
        >
          <input {...getInputProps()} />
          {acceptedFiles.length === 0 && <p>{t('general.upload-drag-drop-message')}</p>}
          {acceptedFiles.length > 0 && (
            <ul>
              {acceptedFiles.map((file) => (
                <li key={file.name}>{file.name}</li>
              ))}
            </ul>
          )}
        </div>
        {showUploadButton && (
          <div>
            <Button
              type="button"
              title="Upload"
              onClick={handleOnSubmitAttachment}
              disabled={loading}
              showLoading
              className="ml-5"
            />
          </div>
        )}
      </div>
    </>
  );
};
